import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

// Token lives in localStorage ("remember me") OR sessionStorage (cleared when the
// browser fully closes). Read from whichever holds it; writing clears the other first.
const TOKEN_KEY = "cs_token";
export function getToken() {
  try { return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY); }
  catch { return null; }
}
export function setToken(token, remember = true) {
  clearToken();
  try { (remember ? localStorage : sessionStorage).setItem(TOKEN_KEY, token); } catch { /* ignore */ }
}
export function clearToken() {
  try { localStorage.removeItem(TOKEN_KEY); sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-retry writes that fail only because the backend is briefly unreachable
// (network drop or a sleeping/booting pod -> 502/503/504). Never retries real
// 4xx errors (validation/auth). Callers can opt out with { _retryDisabled: true }.
const RETRIABLE_STATUS = [502, 503, 504];
api.interceptors.response.use(undefined, async (error) => {
  const config = error.config;
  const status = error.response?.status;
  // Out of AI credits — surface the top-up modal globally and flag the error so
  // callers can skip their own generic toast.
  if (status === 402) {
    const detail = error.response?.data?.detail;
    if (detail && detail.error === "out_of_credits") {
      error.handledOutOfCredits = true;
      window.dispatchEvent(new CustomEvent("codex:out-of-credits", { detail }));
    }
    return Promise.reject(error);
  }
  if (!config || config._retryDisabled) return Promise.reject(error);
  const retriable = !error.response || RETRIABLE_STATUS.includes(status);
  if (!retriable) return Promise.reject(error);
  config._retryCount = (config._retryCount || 0) + 1;
  if (config._retryCount > 3) return Promise.reject(error);
  const delay = Math.min(4000, 500 * 2 ** (config._retryCount - 1));
  await new Promise((r) => setTimeout(r, delay));
  return api(config);
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
